import React from 'react';
import { Calendar, UserCheck, Users } from 'lucide-react';
import { Badge } from './ui/badge';
import { buildNoticeRuleInfo } from '@/utils/leaveUtils';

/**
 * Karar listesi görünümündeki ÖZET satır.
 *
 * Bilerek "aptal" bir bileşendir: hiçbir domain aksiyonu (onayla/reddet/sevk/sil)
 * üretmez, hiçbir hesap yapmaz. Karar yetkisi ve tüm ayrıntı, seçildiğinde sağda
 * açılan mevcut LeaveRequestCard'da kalır. Böylece yetki mantığı tek yerde durur
 * ve e2e sözleşmesi (card-approve-btn) bozulmaz.
 *
 * Gösterilen gün sayısı da BURADA HESAPLANMAZ: backend'in verdiği workdays_used
 * varsa "N iş günü", yoksa total_days ile "N gün", o da yoksa yalnız tarih aralığı
 * yazılır. Kalan izin bakiyesi özet satırda hiç gösterilmez — bakiyenin kanonik
 * hesabı kartın içindedir ve panelde ikinci bir hesap tutmak (daha önce yaşandı)
 * iki sayının birbiriyle çelişmesine yol açıyor.
 */

const ROLE_LABELS = {
  formen: 'Formen',
  bolge_muhendisi: 'Bölge Mühendisi',
  mudur_yardimcisi: 'Müdür Yardımcısı',
  mudur: 'Müdür',
};

const TYPE_STYLES = {
  'Yıllık İzin': 'bg-blue-50 text-blue-800 border-blue-300',
  'Sosyal İzin': 'bg-purple-50 text-purple-800 border-purple-300',
  'Rapor': 'bg-orange-50 text-orange-800 border-orange-300',
};

const formatDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toLocaleDateString('tr-TR');
};

/** Gün ifadesi — yeni hesap YAPMAZ, yalnız var olan alanı biçimlendirir. */
const formatDays = (request) => {
  const workdays = Number(request?.workdays_used);
  if (Number.isFinite(workdays) && workdays > 0) return `${workdays} iş günü`;
  const total = Number(request?.total_days);
  if (Number.isFinite(total) && total > 0) return `${total} gün`;
  return null;
};

const LeaveRequestSummaryRow = ({
  request,
  regionName = null,
  isSelected = false,
  awaitingMyDecision = false,
  onSelect,
}) => {
  const type = request?.reason_type || request?.reason;
  const start = formatDate(request?.start_date);
  const end = formatDate(request?.end_date);
  const returnDate = formatDate(request?.return_date);
  const days = formatDays(request);

  const codes = Array.isArray(request?.review_reason_codes) ? request.review_reason_codes : [];
  const hasSubstitutionConflict = codes.includes('ACTIVE_SUBSTITUTION_CONFLICT');
  const isOffRule = Boolean(request?.review_mode) && request.review_mode !== 'normal';
  // Kart ile AYNI kaynaktan: "Geçmiş gün talebi / Aynı gün talebi" + kural açıklaması.
  const noticeInfo = buildNoticeRuleInfo(request);

  // Durum rozeti — karar bekleyenlerde "kimde beklediği" yazılır.
  let statusNode = null;
  if (request?.status === 'approved') {
    statusNode = <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300 text-xs">Onaylandı</Badge>;
  } else if (request?.status === 'rejected') {
    statusNode = <Badge variant="outline" className="bg-red-50 text-red-700 border-red-300 text-xs">Reddedildi</Badge>;
  } else if (request?.status === 'cancelled') {
    statusNode = <Badge variant="outline" className="bg-gray-100 text-gray-600 border-gray-300 text-xs">İptal edildi</Badge>;
  } else if (awaitingMyDecision) {
    statusNode = <Badge className="bg-blue-600 text-white text-xs">Kararınız bekleniyor</Badge>;
  } else {
    const roleLabel = ROLE_LABELS[request?.current_approver_role] || null;
    statusNode = (
      <Badge variant="outline" className="bg-slate-50 text-slate-600 border-slate-300 text-xs">
        {roleLabel ? `${roleLabel} onayında` : 'İncelemede'}
      </Badge>
    );
  }

  return (
    <button
      type="button"
      onClick={() => onSelect && onSelect(request.id)}
      aria-current={isSelected ? 'true' : undefined}
      className={`w-full rounded-lg border-2 p-3 text-left transition-colors ${
        isSelected
          ? 'border-blue-500 bg-blue-50'
          : 'border-gray-200 bg-white hover:border-gray-400 hover:bg-gray-50'
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="flex min-w-0 items-center gap-1.5">
          <Users className="h-4 w-4 shrink-0 text-gray-500" />
          <span className="truncate text-sm font-semibold text-gray-900">
            {request?.personnel_name || 'İsimsiz'}
          </span>
        </span>
        {type && (
          <Badge
            variant="outline"
            className={`shrink-0 text-xs ${TYPE_STYLES[type] || 'bg-gray-100 text-gray-700 border-gray-300'}`}
          >
            {type}
          </Badge>
        )}
      </div>

      {regionName && (
        <p className="mt-0.5 truncate text-xs text-gray-500">{regionName}</p>
      )}

      {(start || end) && (
        <p className="mt-1.5 flex items-center gap-1.5 text-xs text-gray-700">
          <Calendar className="h-3.5 w-3.5 shrink-0 text-gray-400" />
          <span className="truncate">
            {start} – {end}
            {days ? ` · ${days}` : ''}
          </span>
        </p>
      )}

      {returnDate && (
        <p className="mt-0.5 flex items-center gap-1.5 text-xs text-gray-700">
          <UserCheck className="h-3.5 w-3.5 shrink-0 text-gray-400" />
          <span className="truncate">İşe başlama: {returnDate}</span>
        </p>
      )}

      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        {statusNode}
        {/* Kararı etkileyen uyarılar özette KALIR — detayı açmadan görünmeli. */}
        {hasSubstitutionConflict && (
          <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-300 text-xs">
            ⚠️ Vekalet çakışması
          </Badge>
        )}
        {isOffRule && (
          // Satırın tamamı zaten bir <button>; içine ikinci bir buton konulamaz.
          // Bu yüzden açıklama fareyle üzerine gelince (title) gösterilir, tıklama
          // satırı seçer ve aynı açıklama sağdaki kartta açılabilir hâlde durur.
          <Badge
            variant="outline"
            className="bg-orange-50 text-orange-700 border-orange-300 text-xs cursor-help"
            title={noticeInfo?.note || 'Bu talep standart zaman kuralı dışında açıldı.'}
          >
            {noticeInfo?.label || 'Kural dışı'} <span aria-hidden="true">ⓘ</span>
          </Badge>
        )}
      </div>
    </button>
  );
};

export default React.memo(LeaveRequestSummaryRow);
